// src/pages/proyectos/components/ProyectoFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, Button, Input, Label, Textarea } from '@/components/ui';
import { useCrearProyecto, useActualizarProyecto } from '@/hooks/queries/useProyectos';
import { useClientes } from '@/hooks/queries/useClientes';
import { useUsuarios } from '@/hooks/queries/useUsuarios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import type { Proyecto, ProyectoCreate, ProyectoUpdate } from '@/types';

const schema = z.object({
  codigo: z.string().min(1, 'Código requerido').max(20, 'Máximo 20 caracteres'),
  nombre: z.string().min(1, 'Nombre requerido').max(200, 'Máximo 200 caracteres'),
  descripcion: z.string().optional(),
  cliente_id: z.string().optional(),
  fecha_inicio_planeada: z.string().optional(),
  fecha_fin_planeada: z.string().optional(),
  duracion_sprint_dias: z.coerce.number().min(1).max(60).optional(),
  horas_estimadas: z.coerce.number().min(0).optional(),
  responsable_id: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  proyecto?: Proyecto;
}

export function ProyectoFormModal({ open, onClose, proyecto }: Props) {
  const { data: clientes } = useClientes();
  const { data: usuarios } = useUsuarios();
  const crearMutation = useCrearProyecto();
  const actualizarMutation = useActualizarProyecto();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      duracion_sprint_dias: 14,
    },
  });

  const clienteId = watch('cliente_id');
  const responsableId = watch('responsable_id');

  useEffect(() => {
    if (proyecto) {
      reset({
        codigo: proyecto.codigo,
        nombre: proyecto.nombre,
        descripcion: proyecto.descripcion ?? '',
        cliente_id: proyecto.cliente_id ?? '',
        fecha_inicio_planeada: proyecto.fecha_inicio_planeada ?? '',
        fecha_fin_planeada: proyecto.fecha_fin_planeada ?? '',
        duracion_sprint_dias: proyecto.duracion_sprint_dias,
        horas_estimadas: proyecto.horas_estimadas ?? undefined,
        responsable_id: proyecto.responsable_id ?? '',
      });
    } else {
      reset({
        codigo: '',
        nombre: '',
        descripcion: '',
        cliente_id: '',
        fecha_inicio_planeada: '',
        fecha_fin_planeada: '',
        duracion_sprint_dias: 14,
        horas_estimadas: undefined,
        responsable_id: '',
      });
    }
  }, [proyecto, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        ...data,
        cliente_id: data.cliente_id || undefined,
        responsable_id: data.responsable_id || undefined,
        fecha_inicio_planeada: data.fecha_inicio_planeada || undefined,
        fecha_fin_planeada: data.fecha_fin_planeada || undefined,
        horas_estimadas: data.horas_estimadas || undefined,
      };

      if (proyecto) {
        await actualizarMutation.mutateAsync({
          id: proyecto.id,
          data: payload as ProyectoUpdate,
        });
      } else {
        await crearMutation.mutateAsync(payload as ProyectoCreate);
      }
      onClose();
    } catch {
      // Error handling is done by react-query
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <h2 className="text-xl font-semibold mb-6">
          {proyecto ? 'Editar Proyecto' : 'Nuevo Proyecto'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Código *</Label>
              <Input
                {...register('codigo')}
                placeholder="PRY-001"
                disabled={!!proyecto}
                className="mt-1"
              />
              {errors.codigo && (
                <p className="text-sm text-red-500 mt-1">{errors.codigo.message}</p>
              )}
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                {...register('nombre')}
                placeholder="Nombre del proyecto"
                className="mt-1"
              />
              {errors.nombre && (
                <p className="text-sm text-red-500 mt-1">{errors.nombre.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label>Descripción</Label>
            <Textarea
              {...register('descripcion')}
              placeholder="Descripción del proyecto..."
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Cliente</Label>
              <Select
                value={clienteId ?? ''}
                onValueChange={(value) => setValue('cliente_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cliente</SelectItem>
                  {clientes?.items?.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Responsable</Label>
              <Select
                value={responsableId ?? ''}
                onValueChange={(value) => setValue('responsable_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin responsable</SelectItem>
                  {usuarios?.map((usuario) => (
                    <SelectItem key={usuario.id} value={usuario.id}>
                      {usuario.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha inicio planeada</Label>
              <Input
                type="date"
                {...register('fecha_inicio_planeada')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Fecha fin planeada</Label>
              <Input
                type="date"
                {...register('fecha_fin_planeada')}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Duración sprint (días)</Label>
              <Input
                type="number"
                {...register('duracion_sprint_dias')}
                min={1}
                max={60}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Horas estimadas</Label>
              <Input
                type="number"
                {...register('horas_estimadas')}
                min={0}
                step={0.5}
                placeholder="0"
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : proyecto ? 'Guardar cambios' : 'Crear proyecto'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
